import { useState, useMemo, useEffect, useCallback } from 'react';
import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import { toast } from 'react-toastify';
import { X, Check, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import {
  format,
  addWeeks,
  subWeeks,
  startOfWeek,
  addDays,
  addMinutes,
  isSameDay,
  isPast,
  startOfDay,
} from 'date-fns';
import {
  type CalendarBusyBlock,
  daysWithBusyEvents,
  FREE_SLOT_LABEL,
  isSlotBlockedByBusy,
} from '../../utils/calendarBusySlots';

interface LiveCalendarProps {
  onClose?: () => void;
  onSaved?: () => void | Promise<void>;
  candidateId?: number;
  editingSlot?: {
    id: number;
    start: Date;
    end: Date;
  } | null;
}

interface SelectedSlot {
  id: string;
  start: Date;
  end: Date;
}

// Generate 30-min slots from 8 AM to 8 PM for a given date
function generateDaySlots(day: Date): { start: Date; end: Date }[] {
  const slots: { start: Date; end: Date }[] = [];
  const base = new Date(day);
  base.setHours(8, 0, 0, 0);
  for (let i = 0; i < 24; i++) {
    const start = addMinutes(base, i * 30);
    const end = addMinutes(start, 30);
    slots.push({ start, end });
  }
  return slots;
}

export default function LiveCalendar({
  onClose,
  onSaved,
  candidateId,
  editingSlot = null,
}: LiveCalendarProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [existingSlots, setExistingSlots] = useState<SelectedSlot[]>([]);
  const [calendarBusySlots, setCalendarBusySlots] = useState<CalendarBusyBlock[]>([]);
  const [overrideConfirm, setOverrideConfirm] = useState<{
    blocking: CalendarBusyBlock;
    start: Date;
    end: Date;
  } | null>(null);
  const [isOverriding, setIsOverriding] = useState(false);

  // Week navigation
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 0 }));

  // Currently viewed day (step 2)
  const [activeDay, setActiveDay] = useState<Date | null>(null);

  // Selected free slots across all days
  const [selectedSlots, setSelectedSlots] = useState<SelectedSlot[]>([]);

  const loadExistingSlots = useCallback(async () => {
    if (!candidateId) {
      setExistingSlots([]);
      return;
    }

    try {
      const response = await axiosInstance.get(
        `${API_ROUTES.CALENDAR.GET_USER_EVENTS}/${candidateId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );

      const existing = Array.isArray(response.data)
        ? response.data
            .filter((slot) => {
              const end = new Date(slot.end_time);
              const isBooked = slot.booked === true || slot.booked === 'true' || slot.booked === 1;
              const isEditing = editingSlot && Number(slot.id) === editingSlot.id;
              return end.getTime() > Date.now() && !isBooked && !isEditing;
            })
            .map((slot) => ({
              id: String(slot.id),
              start: new Date(slot.start_time),
              end: new Date(slot.end_time),
            }))
        : [];

      setExistingSlots(existing);
    } catch (error) {
      console.error('Failed to load existing free slots', error);
      setExistingSlots([]);
    }
  }, [candidateId, editingSlot]);

  useEffect(() => {
    if (!editingSlot) return;
    setWeekStart(startOfWeek(editingSlot.start, { weekStartsOn: 0 }));
    setActiveDay(editingSlot.start);
    setSelectedSlots([
      {
        id: `edit-${editingSlot.id}`,
        start: editingSlot.start,
        end: editingSlot.end,
      },
    ]);
  }, [editingSlot]);

  useEffect(() => {
    loadExistingSlots();
  }, [loadExistingSlots]);

  const loadCalendarBusySlots = useCallback(async () => {
    try {
      const res = await axiosInstance.get(API_ROUTES.LIVECALENDAR.GET_BUSY_SLOTS, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (res.data?.success) {
        setCalendarBusySlots(res.data.data ?? []);
      }
    } catch (error) {
      console.error('Failed to load calendar busy blocks', error);
      setCalendarBusySlots([]);
    }
  }, []);

  useEffect(() => {
    void loadCalendarBusySlots();
  }, [loadCalendarBusySlots]);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const busyDayKeys = useMemo(
    () => daysWithBusyEvents(calendarBusySlots, days),
    [calendarBusySlots, days]
  );

  const activeDaySlots = useMemo(
    () =>
      activeDay
        ? generateDaySlots(activeDay).filter(
            ({ start }) =>
              start.getTime() > Date.now() &&
              !existingSlots.some((slot) => slot.start.getTime() === start.getTime())
          )
        : [],
    [activeDay, existingSlots]
  );

  const slotsOnActiveDay = useMemo(
    () => (activeDay ? selectedSlots.filter((s) => isSameDay(s.start, activeDay)) : []),
    [selectedSlots, activeDay]
  );

  const countForDay = (day: Date) => selectedSlots.filter((s) => isSameDay(s.start, day)).length;

  const isSlotSelected = (start: Date) =>
    selectedSlots.some((s) => s.start.getTime() === start.getTime());

  const handleOverrideBusyBlock = async (busyId: string) => {
    try {
      await axiosInstance.put(
        API_ROUTES.LIVECALENDAR.BUSY_SLOT_UPDATE(busyId),
        { blocksAvailability: false },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );
      await loadCalendarBusySlots();
      toast.info('Calendar block overridden — you can mark this time as available.');
    } catch (error) {
      console.error('Failed to override busy block', error);
      toast.error('Could not override this calendar block.');
    }
  };

  const selectSlot = (start: Date, end: Date) => {
    if (editingSlot) {
      setSelectedSlots([{ id: `edit-${editingSlot.id}`, start, end }]);
      return;
    }
    if (isSlotSelected(start)) {
      setSelectedSlots((prev) => prev.filter((s) => s.start.getTime() !== start.getTime()));
    } else {
      setSelectedSlots((prev) => [...prev, { id: `slot-${start.getTime()}`, start, end }]);
    }
  };

  const toggleSlot = (start: Date, end: Date) => {
    const blocking = isSlotBlockedByBusy(start, end, calendarBusySlots);
    if (blocking) {
      setOverrideConfirm({ blocking, start, end });
      return;
    }
    selectSlot(start, end);
  };

  const confirmOverrideBusyBlock = async () => {
    if (!overrideConfirm) return;
    setIsOverriding(true);
    try {
      await handleOverrideBusyBlock(overrideConfirm.blocking.id);
      selectSlot(overrideConfirm.start, overrideConfirm.end);
      setOverrideConfirm(null);
    } finally {
      setIsOverriding(false);
    }
  };

  const handleSaveSlots = async () => {
    if (!candidateId) {
      toast.error('Unable to save availability: candidate id missing.');
      return;
    }
    if (selectedSlots.length === 0) {
      toast.warning('Please select at least one time slot.');
      return;
    }

    setIsSaving(true);
    try {
      if (editingSlot) {
        const slot = selectedSlots[0];
        await axiosInstance.patch(
          `${API_ROUTES.CALENDAR.UPDATE_EVENT}/${editingSlot.id}`,
          {
            start_time: slot.start.toISOString(),
            end_time: slot.end.toISOString(),
            notes: FREE_SLOT_LABEL,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
          }
        );
        await onSaved?.();
        toast.success('Free slot updated.');
        setSelectedSlots([]);
        await loadExistingSlots();
        onClose?.();
        return;
      }

      await Promise.all(
        selectedSlots.map((slot) =>
          axiosInstance.post(
            API_ROUTES.CALENDAR.CREATE_EVENT,
            {
              candidate_id: candidateId,
              title: FREE_SLOT_LABEL,
              start_time: slot.start.toISOString(),
              end_time: slot.end.toISOString(),
              notes: FREE_SLOT_LABEL,
              syncGoogle: true,
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
              },
            }
          )
        )
      );

      await onSaved?.();
      toast.success(
        `${selectedSlots.length} free slot${selectedSlots.length > 1 ? 's' : ''} saved. Recruiters can now select from these times.`
      );
      setSelectedSlots([]);
      await loadExistingSlots();
      onClose?.();
    } catch (error) {
      console.error('Failed to save free slots', error);
      toast.error(
        editingSlot
          ? 'Failed to update free slot. Please try again.'
          : 'Failed to save free slots. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white shadow-2xl border border-gray-100 w-full max-w-2xl flex flex-col relative overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {editingSlot ? 'Edit Free Time Slot' : 'Add Free Time Slots'}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {editingSlot
              ? 'Choose a new time for this availability slot.'
              : "Pick a day, then select the times you're available. Times blocked by Google Calendar show as unavailable unless you override them."}
          </p>
        </div>
        <button
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:text-green-600 hover:bg-green-50 transition"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Week navigation */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 bg-gray-50">
        <button
          onClick={() => {
            setWeekStart((w) => subWeeks(w, 1));
            setActiveDay(null);
          }}
          className="p-1.5 rounded-lg hover:bg-gray-200 transition"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>
        <span className="text-sm font-medium text-gray-700">
          {format(weekStart, 'MMM d')} – {format(addDays(weekStart, 6), 'MMM d, yyyy')}
        </span>
        <button
          onClick={() => {
            setWeekStart((w) => addWeeks(w, 1));
            setActiveDay(null);
          }}
          className="p-1.5 rounded-lg hover:bg-gray-200 transition"
        >
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {/* Day pills */}
      <div className="grid grid-cols-7 gap-1.5 px-4 py-3 border-b border-gray-100">
        {days.map((day) => {
          const count = countForDay(day);
          const isActive = activeDay && isSameDay(day, activeDay);
          const isPastDay = isPast(startOfDay(addDays(day, 1)));
          const dayKey = day.toISOString().slice(0, 10);
          const hasCalendarBusy = busyDayKeys.has(dayKey);
          return (
            <button
              key={day.toISOString()}
              onClick={() => setActiveDay(isSameDay(day, activeDay ?? new Date(0)) ? null : day)}
              disabled={isPastDay}
              className={[
                'flex flex-col items-center gap-1 py-2 px-1 rounded-xl text-xs font-medium transition relative',
                isPastDay
                  ? 'opacity-35 cursor-not-allowed'
                  : isActive
                    ? 'bg-green-600 text-white shadow'
                    : count > 0
                      ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                      : hasCalendarBusy
                        ? 'bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100'
                        : 'text-gray-600 hover:bg-gray-100',
              ].join(' ')}
            >
              <span className="uppercase tracking-wide" style={{ fontSize: '0.65rem' }}>
                {format(day, 'EEE')}
              </span>
              <span
                className={['text-base font-bold', isActive ? 'text-white' : 'text-gray-800'].join(
                  ' '
                )}
              >
                {format(day, 'd')}
              </span>
              {hasCalendarBusy && !isActive && (
                <span
                  className="text-[0.6rem] font-semibold text-amber-800"
                  title="Has a calendar event (private)"
                >
                  Event
                </span>
              )}
              {count > 0 && (
                <span
                  className={[
                    'text-xs font-semibold rounded-full w-5 h-5 flex items-center justify-center',
                    isActive ? 'bg-white text-green-700' : 'bg-green-600 text-white',
                  ].join(' ')}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Time slot grid for active day */}
      <div
        className="flex-1 overflow-y-auto px-6 py-4"
        style={{ minHeight: '240px', maxHeight: '320px' }}
      >
        {!activeDay ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2 py-10">
            <span className="text-3xl">👆</span>
            <p className="text-sm">Select a day above to see time slots</p>
          </div>
        ) : (
          <>
            <p className="text-sm font-semibold text-gray-700 mb-3">
              {format(activeDay, 'EEEE, MMMM d')} — click slots to mark as free
            </p>
            <p className="text-xs text-gray-500 mb-3">
              Gray slots are blocked by your Google Calendar. Click one to override and offer that
              time. Event names are never shown for privacy.
            </p>
            {activeDaySlots.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                No more available slots to add for this day.
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {activeDaySlots.map(({ start, end }) => {
                  const selected = isSlotSelected(start);
                  const blocked = isSlotBlockedByBusy(start, end, calendarBusySlots);
                  return (
                    <button
                      key={start.toISOString()}
                      onClick={() => void toggleSlot(start, end)}
                      title={blocked ? 'Blocked by calendar — click to override' : undefined}
                      className={[
                        'py-2 px-1 rounded-lg text-xs font-medium border transition text-center',
                        selected
                          ? 'bg-green-600 border-green-600 text-white'
                          : blocked
                            ? 'bg-gray-100 border-gray-300 text-gray-500 line-through'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-green-400 hover:bg-green-50',
                      ].join(' ')}
                    >
                      {format(start, 'h:mm a')}
                    </button>
                  );
                })}
              </div>
            )}
            {slotsOnActiveDay.length > 0 && (
              <p className="text-xs text-green-700 mt-3 font-medium">
                ✓ {slotsOnActiveDay.length} slot
                {slotsOnActiveDay.length > 1 ? 's' : ''} selected for this day
              </p>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
        <p className="text-sm text-gray-500">
          {selectedSlots.length > 0
            ? `${selectedSlots.length} slot${selectedSlots.length > 1 ? 's' : ''} selected total`
            : 'No slots selected yet'}
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveSlots}
            disabled={isSaving || selectedSlots.length === 0}
            className="px-5 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : editingSlot ? (
              <>
                <Check className="w-3.5 h-3.5" />
                Save changes
              </>
            ) : (
              <>
                <Check className="w-3.5 h-3.5" />
                Save {selectedSlots.length > 0 ? `${selectedSlots.length} ` : ''}Slot
                {selectedSlots.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>

      {overrideConfirm && (
        <div
          className="absolute inset-0 z-40 flex items-center justify-center bg-slate-900/40 backdrop-blur-[2px] px-4"
          onClick={() => !isOverriding && setOverrideConfirm(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-gray-100 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start gap-3 px-5 pt-5 pb-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-slate-900">Override busy block?</h3>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  This time overlaps a Google Calendar event (shown as &quot;Busy&quot;). Overriding
                  will let recruiters book this slot. Event names are never shared.
                </p>
                <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  <div className="font-medium text-slate-700">
                    {format(overrideConfirm.start, 'EEEE, MMM d')}
                  </div>
                  <div>
                    {format(overrideConfirm.start, 'h:mm a')} –{' '}
                    {format(overrideConfirm.end, 'h:mm a')}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">
              <button
                type="button"
                onClick={() => setOverrideConfirm(null)}
                disabled={isOverriding}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-100 transition disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmOverrideBusyBlock()}
                disabled={isOverriding}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-70 transition"
              >
                {isOverriding ? (
                  <>
                    <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Overriding…
                  </>
                ) : (
                  'Override and mark free'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
